import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { X, Minus, Plus, ShoppingCart, Trash2, ArrowRight, UserCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, total, count, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (items.length === 0) return;

    // Connexion obligatoire avant paiement
    if (!user) {
      setIsOpen(false);
      navigate("/connexion?next=panier");
      return;
    }

    try {
      const origin = window.location.origin;
      const res = await fetch(`${BACKEND}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart_items: items,
          customer_email: user.email,
          origin_url: origin,
        }),
      });
      if (!res.ok) throw new Error("Erreur lors de la création de la session");
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert("Erreur lors du paiement. Veuillez réessayer.");
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#111113] border-l border-white/10 z-[60] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-[#FF5C00]" />
            <h2 className="text-lg font-black text-white tracking-tight">
              Panier
              {count > 0 && (
                <span className="ml-2 text-xs font-bold bg-[#FF5C00] text-white rounded-full px-2 py-0.5">
                  {count}
                </span>
              )}
            </h2>
          </div>
          <button
            data-testid="cart-close-btn"
            onClick={() => setIsOpen(false)}
            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <ShoppingCart className="w-12 h-12 text-white/10 mb-4" />
              <p className="text-white/40 font-medium">Votre panier est vide</p>
              <p className="text-white/25 text-sm mt-1">
                Ajoutez des services ou accessoires
              </p>
              <button
                onClick={() => setIsOpen(false)}
                className="mt-6 text-sm text-[#FF5C00] hover:underline"
              >
                Parcourir les services
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                data-testid={`cart-item-${item.id}`}
                className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/5"
              >
                {/* Type badge */}
                <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-[#FF5C00] uppercase tracking-wider">
                    {item.type === "repair" ? "Rep." : "Acc."}
                  </span>
                </div>

                {/* Name + controls */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white leading-snug">{item.name}</p>
                  <p className="text-xs text-[#FF5C00] font-bold mt-0.5">
                    {item.price}€
                    {item.quantity > 1 && (
                      <span className="text-white/30 font-normal"> × {item.quantity} = {(item.price * item.quantity).toFixed(0)}€</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                      data-testid={`cart-minus-${item.id}`}
                    >
                      <Minus className="w-3 h-3 text-white/60" />
                    </button>
                    <span className="text-sm font-bold text-white w-4 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                      data-testid={`cart-plus-${item.id}`}
                    >
                      <Plus className="w-3 h-3 text-white/60" />
                    </button>
                  </div>
                </div>

                {/* Remove */}
                <button
                  onClick={() => removeItem(item.id)}
                  className="p-1.5 text-white/20 hover:text-red-400 transition-colors"
                  data-testid={`cart-remove-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-4">
            {/* Recap */}
            <div className="space-y-1.5">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between text-xs text-white/40">
                  <span className="truncate pr-2">{item.name} × {item.quantity}</span>
                  <span className="flex-shrink-0">{(item.price * item.quantity).toFixed(0)}€</span>
                </div>
              ))}
              <div className="pt-2 border-t border-white/10 flex justify-between">
                <span className="text-sm font-bold text-white">Total</span>
                <span className="text-xl font-black text-[#FF5C00]">{total.toFixed(0)}€</span>
              </div>
            </div>

            {/* Checkout CTA */}
            {!user ? (
              <button
                data-testid="checkout-login-required-btn"
                onClick={() => { setIsOpen(false); navigate("/connexion?next=panier"); }}
                className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-bold py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#FF5C00]/25"
              >
                <UserCircle className="w-4 h-4" />
                Se connecter pour payer
              </button>
            ) : (
              <button
                data-testid="checkout-btn"
                onClick={handleCheckout}
                className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-bold py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#FF5C00]/25"
              >
                Payer {total.toFixed(0)}€
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={() => { clearCart(); setIsOpen(false); }}
              className="w-full text-xs text-white/30 hover:text-white/50 transition-colors"
              data-testid="cart-clear-btn"
            >
              Vider le panier
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
